/* ==========================================================================
   Pricing Section Component
   Task F33: Landing page pricing section with billing toggle + tier cards
   ========================================================================== */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { BillingToggle } from './BillingToggle'
import { TierCard, type TierInfo } from './TierCard'

/* --------------------------------------------------------------------------
   Tier Data (matches backend TIER_CONFIG in app/billing/tiers.py)
   Prices in dollars. Overage rate: $0.30/script.
   -------------------------------------------------------------------------- */

const TIERS: TierInfo[] = [
  {
    name: 'Starter',
    monthlyPrice: 299,
    annualPrice: 3000,
    scriptLimit: 1000,
    overageRate: '$0.30',
    features: [
      'White-labeled provider portal',
      'Custom product catalog',
      'Automated structured faxes',
      'Provider management',
      'Email support',
    ],
  },
  {
    name: 'Growth',
    monthlyPrice: 799,
    annualPrice: 8000,
    scriptLimit: 3000,
    overageRate: '$0.30',
    features: [
      'Everything in Starter',
      'Priority onboarding',
      'Custom branding & colors',
      'Provider analytics dashboard',
      'Priority email & chat support',
    ],
  },
  {
    name: 'Scale',
    monthlyPrice: 1499,
    annualPrice: 15000,
    scriptLimit: 6000,
    overageRate: '$0.30',
    features: [
      'Everything in Growth',
      'Dedicated account manager',
      'API access',
      'Custom integrations',
      'Phone support & SLA',
    ],
  },
]

export function PricingSection() {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual')

  return (
    <section id="pricing" className="py-16 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div className="text-center mb-10">
          <h2 className="font-serif text-2xl sm:text-3xl text-charcoal mb-4">
            Simple, transparent pricing
          </h2>
          <p className="text-charcoal/70 max-w-2xl mx-auto mb-8">
            Choose the plan that fits your pharmacy's volume. All plans include
            the full platform — no hidden fees.
          </p>

          {/* Billing toggle */}
          <BillingToggle billingCycle={billingCycle} onChange={setBillingCycle} />
        </div>

        {/* Tier cards */}
        <div className="grid md:grid-cols-3 gap-8 items-start">
          {TIERS.map((tier, index) => (
            <TierCard
              key={tier.name}
              tier={tier}
              billingCycle={billingCycle}
              isPopular={index === 1}
            />
          ))}
        </div>

        {/* Questions link */}
        <div className="text-center mt-10">
          <p className="text-charcoal/60 text-sm">
            Have questions?{' '}
            <Link
              to="/contact-sales"
              className="text-deep-purple hover:underline font-medium"
            >
              Talk to our sales team
            </Link>
          </p>
        </div>
      </div>
    </section>
  )
}

export default PricingSection
